import React, { useEffect, useState } from 'react';
import SharedStateBridge, { SharedStateType } from '@sendible/shared-state-bridge';
import { fetchWithHeaders } from '@sendible/common';
import { createGlobalStyle } from 'styled-components';
import Editor from './photoEditor';
import { getUrlWithCredentialsFromUser } from '../../utils';
import { EDITOR_OPEN } from '.';

const getReducedImage = async (imgTag: HTMLImageElement, reduction: number): Promise<{ uri: string; width: number; height: number }> => {
  // Create canvas for scaling
  const canvas = document.createElement('canvas');

  canvas.width = imgTag.naturalWidth / reduction;
  canvas.height = imgTag.naturalHeight / reduction;

  // paint scaled image in canvas
  const ctx = canvas.getContext('2d');

  if (ctx) {
    ctx.drawImage(imgTag, 0, 0, canvas.width, canvas.height);
  }

  // to blob is async so we need to wait for the promise to resolve
  return new Promise((res, rej) => {
    canvas.toBlob((blob) => {
      if (blob) {
        const blobUrl = URL.createObjectURL(blob);

        res({
          height: canvas.height,
          width: canvas.width,
          uri: blobUrl,
        });
      }
      rej();
    }, 'image/jpeg');
  });
};

const PhotoInitialiser = () => {
  const [preloadEditor, setPreloadEditor] = useState<boolean>(false);
  const [data, setData] = useState<OpenEditorDataType>({} as OpenEditorDataType);

  const GlobalStyle = createGlobalStyle`
    body.creative-editor-open {
      margin: 0;
      overflow: hidden;
    }
`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getImageSourceSets = (eventData: any) => {
    const imageTag = new Image();

    imageTag.crossOrigin = 'anonymous';
    imageTag.onload = async () => {
      // if the request works and the image loads, we then open the editor
      // but before opening the editor, we need to resize the image to pass different sizes to it,
      // as the editor doesn't perform good with large images, they provide the option of using "source-sets"
      const quarterSizeImage = await getReducedImage(imageTag, 4);
      const halfSizeImage = await getReducedImage(imageTag, 2);
      const originalImage = {
        height: imageTag.height,
        width: imageTag.width,
        uri: imageTag.src,
      };

      setData({
        originalImage,
        quarterSizeImage,
        halfSizeImage,
        serviceSet: eventData.serviceSet,
        restrictedServices: eventData.restrictedServices,
        domainUrl: eventData.domainUrl,
        user: eventData.user,
        originalMediaId: eventData.originalMediaId,
        fileName: eventData.fileName,
        isNewMediaSystem: false,
      });
    };

    imageTag.onerror = async () => {
      const response = await fetchWithHeaders({
        method: 'GET',
        url: getUrlWithCredentialsFromUser(`https://${WEBPACK_API}/api/v2/content_proxy.json`, eventData.user),
        params: {
          url: eventData.imageUrl,
          isBase64: 'true',
        },
      });

      const { data: responseData } = response as { data?: string };

      if (responseData) {
        // if the endpoint returns the base64 string successfully,
        // then we convert it to a blob url, as the editor doesn't accept base64 as urls.
        const byteString = atob((response as { data?: string }).data!.split(',')[1]); // we need to remove the 'data:image/jpeg;base64,' prefix
        const arrayBuffer = new Uint8Array(byteString.length);

        for (let i = 0; i < byteString.length; i++) {
          arrayBuffer[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
        const blobURL = URL.createObjectURL(blob);

        imageTag.src = blobURL;
      }
    };

    imageTag.src = eventData.imageUrl;
  };

  useEffect(() => {
    document.body.classList.add('creative-editor-open');

    return () => {
      document.body.classList.remove('creative-editor-open');
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { eventName, data: eventData } = event.data;

      if (eventName === 'preload-editor' && !preloadEditor) {
        setPreloadEditor(true);
      }

      if (eventName === EDITOR_OPEN && eventData) {
        SharedStateBridge.setItem({ user: eventData.user, compose: {}, app: {} } as SharedStateType);

        window.pendo.initialize({
          visitor: eventData.pendoUserData,
          account: eventData.pendoAccountData,
        });

        if (eventData.imageUrl) {
          getImageSourceSets(eventData);
        } else {
          // when data is set, the editor opens
          setData({
            media: eventData.media,
            serviceSet: eventData.serviceSet,
            restrictedServices: eventData.restrictedServices,
            domainUrl: eventData.domainUrl,
            user: eventData.user,
            originalMediaId: eventData.originalMediaId,
            fileName: eventData.fileName,
            isNewMediaSystem: true,
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // do not load the Editor component until the preload flag is set
  // this ensure any performance hit from initialising CESDK
  // is delayed unit the feature is ready to use
  if (preloadEditor && (data.media || data.originalImage)) {
    return (
      <React.StrictMode>
        <GlobalStyle />
        <Editor
          data={data}
          resetState={() => {
            setData({} as OpenEditorDataType);
          }}
        />
      </React.StrictMode>
    );
  }

  return null;
};

export default PhotoInitialiser;
